import { useEffect, useRef } from 'react';

interface DNAHelixOverlayProps {
  opacity?: number;
}

export default function DNAHelixOverlay({ opacity = 0.8 }: DNAHelixOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let time = 0;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();

    // Parse CSS color string (hex or rgb/rgba) to [r,g,b]
    const parseCssColorToRgb = (input: string): [number, number, number] => {
      let c = input.trim();
      if (!c) return [10, 116, 255];
      const rgbMatch = c.match(/^rgba?\(([^)]+)\)$/i);
      if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map(v => parseFloat(v.trim()));
        return [
          Math.max(0, Math.min(255, parts[0] || 0)),
          Math.max(0, Math.min(255, parts[1] || 0)),
          Math.max(0, Math.min(255, parts[2] || 0)),
        ];
      }
      if (c.startsWith('#')) c = c.slice(1);
      if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
      if (c.length !== 6) return [10, 116, 255];
      const r = parseInt(c.slice(0,2), 16);
      const g = parseInt(c.slice(2,4), 16);
      const b = parseInt(c.slice(4,6), 16);
      return [r,g,b];
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 0.02;

      // Read header accent each frame to respond to theme/accent changes
      const accent = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-header-accent')
        .trim() || '#0A74FF';
      const [r,g,b] = parseCssColorToRgb(accent);

      const helixCount = 3;
      const spacing = canvas.width / (helixCount + 1);

      for (let h = 0; h < helixCount; h++) {
        const offsetX = spacing * (h + 1);
        
        for (let i = 0; i < 50; i++) {
          const y = (i / 50) * canvas.height;
          const angle1 = time + (i * 0.2) + (h * Math.PI / 3);
          const angle2 = angle1 + Math.PI;
          
          const x1 = offsetX + Math.sin(angle1) * 100;
          const x2 = offsetX + Math.sin(angle2) * 100;
          
          const gradient1 = ctx.createRadialGradient(x1, y, 0, x1, y, 8);
          gradient1.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.8)`);
          gradient1.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
          
          const gradient2 = ctx.createRadialGradient(x2, y, 0, x2, y, 8);
          gradient2.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.8)`);
          gradient2.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
          
          ctx.beginPath();
          ctx.arc(x1, y, 8, 0, Math.PI * 2);
          ctx.fillStyle = gradient1;
          ctx.fill();
          
          ctx.beginPath();
          ctx.arc(x2, y, 8, 0, Math.PI * 2);
          ctx.fillStyle = gradient2;
          ctx.fill();
          
          if (i < 49) {
            const nextY = ((i + 1) / 50) * canvas.height;
            const nextAngle1 = time + ((i + 1) * 0.2) + (h * Math.PI / 3);
            const nextAngle2 = nextAngle1 + Math.PI;
            const nextX1 = offsetX + Math.sin(nextAngle1) * 100;
            const nextX2 = offsetX + Math.sin(nextAngle2) * 100;
            
            ctx.beginPath();
            ctx.moveTo(x1, y);
            ctx.lineTo(nextX1, nextY);
            ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.3)`;
            ctx.lineWidth = 2;
            ctx.stroke();
            
            ctx.beginPath();
            ctx.moveTo(x2, y);
            ctx.lineTo(nextX2, nextY);
            ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.3)`;
            ctx.lineWidth = 2;
            ctx.stroke();
          }
          
          if (i % 3 === 0) {
            ctx.beginPath();
            ctx.moveTo(x1, y);
            ctx.lineTo(x2, y);
            ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.2)`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      resizeCanvas();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [opacity]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -1,
        opacity: opacity
      }}
    />
  );
}
